import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Lock, UserRound, KeyRound, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const MAX_ATTEMPTS = 3;
const COOLDOWN_ESCALATION = [30, 60, 120]; // seconds: 30s, 1min, 2min

export function ExclusiveLoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [cooldownEnd, setCooldownEnd] = useState<number | null>(null);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    const cooldownRef = useRef<number | null>(null);

    // Cooldown countdown timer
    useEffect(() => {
        if (!cooldownEnd) { setCooldownRemaining(0); return; }
        const tick = () => {
            const remaining = Math.ceil((cooldownEnd - Date.now()) / 1000);
            if (remaining <= 0) {
                setCooldownEnd(null);
                setCooldownRemaining(0);
            } else {
                setCooldownRemaining(remaining);
            }
        };
        tick();
        cooldownRef.current = window.setInterval(tick, 1000);
        return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
    }, [cooldownEnd]);

    const startCooldown = useCallback((attempts: number) => {
        const tier = Math.min(Math.floor((attempts - 1) / MAX_ATTEMPTS), COOLDOWN_ESCALATION.length - 1);
        const seconds = COOLDOWN_ESCALATION[tier];
        setCooldownEnd(Date.now() + seconds * 1000);
    }, []);

    const isLocked = cooldownRemaining > 0;

    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) navigate('/team-selection', { replace: true });
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (isLocked) return;
        if (!username.trim() || !password.trim()) {
            setError('Please enter both username and password.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password }),
            });
            const data = await res.json();
            if (!res.ok) {
                if (res.status === 429) {
                    setError('Too many login attempts. Please wait a minute and try again.');
                    return;
                }
                const newAttempts = failedAttempts + 1;
                setFailedAttempts(newAttempts);
                if (newAttempts >= MAX_ATTEMPTS) {
                    startCooldown(newAttempts);
                    setError(`Too many failed attempts. Please wait.`);
                } else {
                    setError(data.message || 'Invalid credentials.');
                }
                return;
            }
            setFailedAttempts(0);
            login(data.access_token, data.user);
            navigate('/team-selection', { replace: true });
        } catch {
            setError('Connection failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0e1a 0%, #0f1629 40%, #1a1040 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: "'Inter', -apple-system, sans-serif",
        }}>
            {/* Subtle grid background */}
            <div style={{
                position: 'fixed', inset: 0, opacity: 0.03,
                backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
                pointerEvents: 'none',
            }} />

            <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px' }}>
                {/* Back link */}
                <Link
                    to="/login"
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        color: '#64748b', fontSize: '13px', textDecoration: 'none',
                        marginBottom: '24px', transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#94a3b8')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}
                >
                    <ArrowLeft size={14} /> Back to main login
                </Link>

                {/* Card */}
                <div style={{
                    background: 'rgba(15, 20, 35, 0.85)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(123, 140, 255, 0.15)',
                    borderRadius: '16px',
                    padding: '40px 36px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(123, 140, 255, 0.05)',
                }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{
                            width: '48px', height: '48px', borderRadius: '12px',
                            background: 'linear-gradient(135deg, rgba(123, 140, 255, 0.15), rgba(139, 92, 246, 0.15))',
                            border: '1px solid rgba(123, 140, 255, 0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}>
                            <Lock size={22} style={{ color: '#7b8cff' }} />
                        </div>
                        <h1 style={{
                            color: 'white', fontSize: '22px', fontWeight: 800,
                            margin: '0 0 6px', letterSpacing: '-0.5px',
                        }}>
                            Exclusive Access
                        </h1>
                        <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                            Sign in with your credentials
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Username */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                color: '#94a3b8', fontSize: '12px', fontWeight: 600,
                                marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>
                                <UserRound size={13} /> Email or username
                            </label>
                            <input
                                type="text"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                placeholder="Test-user email or admin username"
                                autoComplete="username"
                                style={{
                                    width: '100%', padding: '12px 14px',
                                    background: 'rgba(30, 40, 65, 0.6)',
                                    border: '1px solid rgba(100, 116, 139, 0.2)',
                                    borderRadius: '10px', color: 'white', fontSize: '14px',
                                    outline: 'none', transition: 'border-color 0.2s',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={e => e.currentTarget.style.borderColor = 'rgba(123, 140, 255, 0.4)'}
                                onBlur={e => e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.2)'}
                            />
                        </div>

                        {/* Password */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                color: '#94a3b8', fontSize: '12px', fontWeight: 600,
                                marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>
                                <KeyRound size={13} /> Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••••••"
                                    autoComplete="current-password"
                                    style={{
                                        width: '100%', padding: '12px 42px 12px 14px',
                                        background: 'rgba(30, 40, 65, 0.6)',
                                        border: '1px solid rgba(100, 116, 139, 0.2)',
                                        borderRadius: '10px', color: 'white', fontSize: '14px',
                                        outline: 'none', transition: 'border-color 0.2s',
                                        boxSizing: 'border-box',
                                    }}
                                    onFocus={e => e.currentTarget.style.borderColor = 'rgba(123, 140, 255, 0.4)'}
                                    onBlur={e => e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.2)'}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute', right: '12px', top: '50%',
                                        transform: 'translateY(-50%)', background: 'none',
                                        border: 'none', color: '#64748b', cursor: 'pointer',
                                        padding: '2px', display: 'flex',
                                    }}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* Error */}
                        {error && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '8px', padding: '10px 14px',
                                color: '#fca5a5', fontSize: '13px',
                                marginBottom: '16px',
                            }}>
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading || isLocked}
                            style={{
                                width: '100%', padding: '13px',
                                background: (loading || isLocked)
                                    ? 'rgba(123, 140, 255, 0.2)'
                                    : 'linear-gradient(135deg, #5f73ff, #7b8cff)',
                                border: 'none', borderRadius: '10px',
                                color: 'white', fontSize: '15px', fontWeight: 700,
                                cursor: (loading || isLocked) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                letterSpacing: '0.3px',
                                opacity: isLocked ? 0.5 : 1,
                            }}
                        >
                            {isLocked ? (
                                `Locked — ${cooldownRemaining}s`
                            ) : loading ? (
                                <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Logging in...</>
                            ) : (
                                'Login'
                            )}
                        </button>
                    </form>
                </div>

                {/* Subtle branding */}
                <div style={{
                    textAlign: 'center', marginTop: '24px',
                    color: '#334155', fontSize: '12px', fontWeight: 600,
                    letterSpacing: '2px', fontStyle: 'italic',
                }}>
                    STRATSYNC
                </div>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                input::placeholder { color: #475569; }
            `}</style>
        </div>
    );
}
